import { useEffect, useState, type ReactNode } from "react";
import { Link } from "@inertiajs/react";
import { CalendarDays, ChevronRight } from "lucide-react";
import { Menu } from "@/components/Menu";
import { Button } from "@/components/Button";
import { cn } from "@/lib/cn";

export interface BreadcrumbItem {
  label: string;
  to?: string;
}

export const TIME_RANGES = [
  { id: "7d",   label: "Last 7 days",   days: 7   },
  { id: "30d",  label: "Last 30 days",  days: 30  },
  { id: "90d",  label: "Last 90 days",  days: 90  },
  { id: "ytd",  label: "Year to date",  days: 365 },
  { id: "12mo", label: "Last 12 months", days: 365 },
  { id: "all",  label: "All time",      days: 0   },
] as const;
export type TimeRangeId = (typeof TIME_RANGES)[number]["id"];

export interface ImpactDashboardTemplateProps {
  title: ReactNode;
  subtitle?: ReactNode;
  breadcrumbs?: BreadcrumbItem[];
  /** Right-side actions (Export, Share, etc.) */
  actions?: ReactNode;
  /** Filter row rendered below the header. */
  filters?: ReactNode;
  /**
   * Optional full-bleed hero rendered above the standard header — used by
   * the Home page for the ImpactHero. When provided, the header sits below.
   */
  hero?: ReactNode;
  /** When true, hides the time-range selector (useful when hero owns it). */
  hideTimeRange?: boolean;
  onTimeRangeChange?: (range: TimeRangeId) => void;
  defaultTimeRange?: TimeRangeId;
  children: ReactNode;
}

export function ImpactDashboardTemplate({
  title,
  subtitle,
  breadcrumbs,
  actions,
  filters,
  hero,
  hideTimeRange,
  onTimeRangeChange,
  defaultTimeRange = "30d",
  children,
}: ImpactDashboardTemplateProps) {
  // Phase 1: time range is local UI state only (not yet synced to the URL or
  // backed by a real query). Wiring it to Inertia query params comes later.
  const [range, setRange] = useState<TimeRangeId>(defaultTimeRange);
  const rangeMeta = TIME_RANGES.find((r) => r.id === range) ?? TIME_RANGES[1]!;

  useEffect(() => {
    onTimeRangeChange?.(range);
  }, [range, onTimeRangeChange]);

  useEffect(() => {
    const previous = document.title;
    const titleStr = typeof title === "string" ? title : "Portal";
    document.title = `${titleStr} · stabiliti`;
    return () => {
      document.title = previous;
    };
  }, [title]);

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex-1 overflow-auto">
        <div className="mx-auto w-full max-w-[1440px] px-4 py-5 sm:px-6 sm:py-6">
          {breadcrumbs && breadcrumbs.length > 0 && (
            <nav aria-label="Breadcrumb" className="mb-3 flex items-center gap-1 text-xs text-fg-subtle">
              {breadcrumbs.map((bc, i) => (
                <span key={i} className="flex items-center gap-1">
                  {i > 0 && <ChevronRight className="size-3" />}
                  {bc.to ? (
                    <Link href={bc.to} className="hover:text-fg">{bc.label}</Link>
                  ) : (
                    <span className={cn(i === breadcrumbs.length - 1 && "text-fg")}>
                      {bc.label}
                    </span>
                  )}
                </span>
              ))}
            </nav>
          )}

          {hero && <div className="mb-6">{hero}</div>}

          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <div className="min-w-0">
              <h1 className="font-brand text-2xl font-semibold tracking-tight text-fg">
                {title}
              </h1>
              {subtitle && (
                <p className="mt-1 text-sm text-fg-muted">{subtitle}</p>
              )}
            </div>
            <div className="flex items-center gap-2 sm:shrink-0">
              {!hideTimeRange && (
                <Menu.Root>
                  <Menu.Trigger asChild>
                    <Button intent="secondary" size="md">
                      <CalendarDays />
                      {rangeMeta.label}
                    </Button>
                  </Menu.Trigger>
                  <Menu.Content align="end">
                    <Menu.Label>Time range</Menu.Label>
                    {TIME_RANGES.map((r) => (
                      <Menu.CheckboxItem
                        key={r.id}
                        checked={range === r.id}
                        onCheckedChange={() => setRange(r.id)}
                      >
                        {r.label}
                      </Menu.CheckboxItem>
                    ))}
                  </Menu.Content>
                </Menu.Root>
              )}
              {actions}
            </div>
          </div>

          {filters && (
            <div className="mt-4 flex flex-wrap items-center gap-2">{filters}</div>
          )}

          <div className="mt-6">{children}</div>
        </div>
      </div>
    </div>
  );
}

/** Bento grid container (12-col at lg+). */
export function ImpactGrid({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4 lg:grid-cols-12",
        className,
      )}
    >
      {children}
    </div>
  );
}

interface ImpactTileProps {
  col?: 3 | 4 | 6 | 8 | 12;
  row?: 1 | 2;
  index?: number;
  children: ReactNode;
  className?: string;
}

const COL_CLASS: Record<NonNullable<ImpactTileProps["col"]>, string> = {
  3:  "lg:col-span-3",
  4:  "lg:col-span-4",
  6:  "lg:col-span-6",
  8:  "lg:col-span-8",
  12: "lg:col-span-12 col-span-2 md:col-span-4",
};

const ROW_CLASS: Record<NonNullable<ImpactTileProps["row"]>, string> = {
  1: "lg:row-span-1",
  2: "lg:row-span-2",
};

export function ImpactTile({
  col = 4,
  row = 1,
  index = 0,
  className,
  children,
}: ImpactTileProps) {
  return (
    <div
      className={cn(
        "col-span-2 md:col-span-2",
        COL_CLASS[col],
        ROW_CLASS[row],
        "animate-in fade-in slide-in-from-bottom-3 duration-500 ease-out",
        className,
      )}
      style={{
        animationDelay: `${index * 60}ms`,
        animationFillMode: "both",
      }}
    >
      {children}
    </div>
  );
}
